/**
 * DuplicateCleanupPanel — v2.9.99 (2026-06-02)
 * ──────────────────────────────────────────────────────────────
 * Καθαρή έκδοση. Σύμφωνα με τη ρητή οδηγία του operator:
 *  - Καμία λογική για cover/preview backfill (το Shazam recognition
 *    γράφει πλέον cover_art_url & preview_url ΑΠΕΥΘΕΙΑΣ στα νέα plays
 *    — v2.9.99 fix στο stream_monitor_service.py).
 *  - Μόνο: (1) status του nightly automatic cleanup, (2) duplicate
 *    cleanup με dry-run / live / auto-loop.
 *  - Όλα τα admin buttons για mass-fill / iTunes / canonical-key
 *    backfill / title-cleanup ΕΧΟΥΝ ΑΦΑΙΡΕΘΕΙ.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  AlertTriangle, CheckCircle2, Clock, Loader2, Play, Search, Sparkles, Zap,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DuplicateCleanupPanel = ({ token }) => {
  const [gapMinutes, setGapMinutes] = useState(15);
  const [daysBack, setDaysBack] = useState(30);
  const [onlyShazamOnly, setOnlyShazamOnly] = useState(false);
  const [maxPairs, setMaxPairs] = useState(50000);

  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loopProgress, setLoopProgress] = useState(null);
  const [nightlyStatus, setNightlyStatus] = useState(null);

  // Fetch nightly cleanup status every 60s
  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const res = await axios.get(
          `${API}/api/admin/backfill/nightly-cleanup/status`,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 10 * 1000 },
        );
        if (!cancelled) setNightlyStatus(res.data);
      } catch (e) { /* best-effort */ }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token]);

  const runNightlyNow = async () => {
    const ok = window.confirm(
      'Τρέξε άμεσα τον νυχτερινό καθαρισμό διπλών εγγραφών;\n\n' +
      'Εκτελείται στο background. Δες την πρόοδο στο status card.',
    );
    if (!ok) return;
    try {
      await axios.post(
        `${API}/api/admin/backfill/nightly-cleanup/run-now`,
        null,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 10 * 1000 },
      );
      setTimeout(async () => {
        try {
          const res = await axios.get(
            `${API}/api/admin/backfill/nightly-cleanup/status`,
            { headers: { Authorization: `Bearer ${token}` }, timeout: 10 * 1000 },
          );
          setNightlyStatus(res.data);
        } catch (e) { /* ignore */ }
      }, 1500);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const runCleanup = async (dryRun) => {
    if (!dryRun) {
      const ok = window.confirm(
        `Σίγουρα θέλεις να κάνεις ΖΩΝΤΑΝΟ καθάρισμα διπλών στη βάση;\n\n` +
        `Παράμετροι:\n` +
        `  • gap_minutes: ${gapMinutes}\n` +
        `  • days_back: ${daysBack}\n` +
        `  • only_shazam_only: ${onlyShazamOnly ? 'Ναι' : 'Όχι (όλοι οι σταθμοί)'}\n` +
        `  • max_pairs: ${maxPairs}\n\n` +
        `Η ενέργεια είναι ΜΗ-αναστρέψιμη: τα διπλά plays θα συγχωνευθούν στο πρωτότυπο record.`,
      );
      if (!ok) return;
    }
    setLoading(true);
    setMode(dryRun ? 'dry' : 'live');
    setResult(null);
    setError(null);
    setLoopProgress(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/backfill/cleanup-duplicates`,
        null,
        {
          params: {
            gap_minutes: gapMinutes,
            days_back: daysBack,
            dry_run: dryRun,
            only_shazam_only: onlyShazamOnly,
            max_pairs: maxPairs,
          },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10 * 60 * 1000,
        },
      );
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const runCleanupLoop = async () => {
    const ok = window.confirm(
      'ΑΥΤΟΜΑΤΟΣ ΚΑΘΑΡΙΣΜΟΣ μέχρι 0 duplicates remaining.\n\n' +
      'Η εφαρμογή θα καλέσει το endpoint επανειλημμένα\n' +
      '(έως 20 φορές) μέχρι να μην βρίσκει άλλα διπλά.',
    );
    if (!ok) return;
    setLoading(true);
    setMode('cleanup-loop');
    setResult(null);
    setError(null);
    let totalMerged = 0;
    let totalRecovered = 0;
    let rounds = 0;
    let lastRes = null;
    try {
      for (let i = 0; i < 20; i++) {
        rounds += 1;
        setLoopProgress({ rounds, totalMerged, totalRecovered, status: 'running' });
        const res = await axios.post(
          `${API}/api/admin/backfill/cleanup-duplicates`,
          null,
          {
            params: {
              gap_minutes: gapMinutes,
              days_back: daysBack,
              dry_run: false,
              only_shazam_only: onlyShazamOnly,
              max_pairs: maxPairs,
            },
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10 * 60 * 1000,
          },
        );
        lastRes = res.data;
        totalMerged += res.data.duplicates_merged || 0;
        totalRecovered += res.data.plays_recovered || 0;
        if ((res.data.duplicate_pairs_found || 0) === 0) break;
      }
      setLoopProgress({ rounds, totalMerged, totalRecovered, status: 'done' });
      setResult({
        ...(lastRes || {}),
        duplicates_merged: totalMerged,
        plays_recovered: totalRecovered,
        _loop_rounds: rounds,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="duplicate-cleanup-panel">
      {/* Nightly automated cleanup status card */}
      <div className="rounded-lg border border-indigo-300 dark:border-indigo-700 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-4" data-testid="nightly-cleanup-status">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <Clock className="w-5 h-5 text-indigo-600 dark:text-indigo-300 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-indigo-900 dark:text-indigo-100">
                🌙 Αυτόματος Νυχτερινός Καθαρισμός Διπλών
              </h3>
              <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-0.5">
                Τρέχει κάθε βράδυ στις <strong>21:30 Ώρα Ελλάδος</strong> (DST-safe).
                Σαρώνει τα τραγούδια των τελευταίων 30 ημερών και συγχωνεύει διπλά plays εντός 15-λεπτου παραθύρου.
              </p>
              {nightlyStatus && nightlyStatus.last_status && nightlyStatus.last_status !== 'never_run' ? (
                <div className="mt-2 text-xs space-y-0.5">
                  <div className="text-indigo-700 dark:text-indigo-300">
                    Τελευταίο run: <strong>{nightlyStatus.last_finished_at ? new Date(nightlyStatus.last_finished_at).toLocaleString('el-GR') : '—'}</strong>
                    {' · '}
                    <span className={
                      nightlyStatus.last_status === 'ok' ? 'text-emerald-700 dark:text-emerald-300' :
                      nightlyStatus.last_status === 'running' ? 'text-blue-700 dark:text-blue-300' :
                      'text-red-700 dark:text-red-300'
                    }>
                      {nightlyStatus.last_status === 'ok' ? '✓ Επιτυχία' :
                       nightlyStatus.last_status === 'running' ? '⏳ Σε εξέλιξη' :
                       '✗ Σφάλμα'}
                    </span>
                    {nightlyStatus.last_summary?.total_duration_s && (
                      <span className="text-indigo-600 dark:text-indigo-400">
                        {' · '}διάρκεια {nightlyStatus.last_summary.total_duration_s}s
                      </span>
                    )}
                  </div>
                  {nightlyStatus.last_summary?.steps && (
                    <div className="text-indigo-600 dark:text-indigo-400 font-mono text-[11px]">
                      {nightlyStatus.last_summary.steps
                        .filter(s => s.step === 'duplicate_cleanup_loop')
                        .map((step, i) => (
                          <span key={i}>
                            🔁 διπλά συγχωνεύτηκαν: <strong>{step.total_merged || 0}</strong> σε {step.rounds} γύρους
                          </span>
                        ))
                      }
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-1.5 text-xs text-indigo-600 dark:text-indigo-400 italic">
                  Δεν έχει τρέξει ακόμα. Θα τρέξει αυτόματα στις 21:30.
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={runNightlyNow}
            data-testid="nightly-run-now-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium whitespace-nowrap"
            title="Εκτέλεση τώρα (χωρίς να περιμένει 21:30)"
          >
            <Zap className="w-3.5 h-3.5" />
            Τρέξε τώρα
          </button>
        </div>
      </div>

      {/* Banner — context */}
      <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-amber-900 dark:text-amber-100">
              🎯 Χειροκίνητος καθαρισμός διπλών (Shazam + ICY)
            </h3>
            <p className="text-sm text-amber-800 dark:text-amber-200 mt-0.5">
              Συγχωνεύει διπλά plays στο ίδιο τραγούδι/σταθμό εντός χρονικού παραθύρου.
              <strong> Πάντα τρέξε πρώτα Δοκιμή (dry run)</strong>.
              Το ζωντανό καθάρισμα είναι μη αναστρέψιμο.
            </p>
          </div>
        </div>
      </div>

      {/* Params */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Χρονικό παράθυρο (λεπτά)</span>
          <input type="number" min="2" max="60" value={gapMinutes}
            onChange={(e) => setGapMinutes(parseInt(e.target.value, 10) || 15)}
            data-testid="cleanup-gap-minutes"
            className="mt-1 w-full px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Ιστορικό (ημέρες πίσω)</span>
          <input type="number" min="1" max="90" value={daysBack}
            onChange={(e) => setDaysBack(parseInt(e.target.value, 10) || 30)}
            data-testid="cleanup-days-back"
            className="mt-1 w-full px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Μέγιστα ζεύγη</span>
          <input type="number" min="1" max="100000" value={maxPairs}
            onChange={(e) => setMaxPairs(parseInt(e.target.value, 10) || 50000)}
            data-testid="cleanup-max-pairs"
            className="mt-1 w-full px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700" />
        </label>
        <label className="flex items-center gap-2 pt-5">
          <input type="checkbox" checked={onlyShazamOnly}
            onChange={(e) => setOnlyShazamOnly(e.target.checked)}
            data-testid="cleanup-only-shazam" className="w-4 h-4" />
          <span className="text-xs text-gray-700 dark:text-gray-300">Μόνο Shazam-only σταθμοί</span>
        </label>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={() => runCleanup(true)} disabled={loading}
          data-testid="cleanup-dry-run-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium">
          {loading && mode === 'dry' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          1. Δοκιμή (Dry Run)
        </button>
        <button type="button" onClick={() => runCleanup(false)} disabled={loading}
          data-testid="cleanup-live-run-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium">
          {loading && mode === 'live' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          2. Καθάρισμα (Live)
        </button>
        <button type="button" onClick={runCleanupLoop} disabled={loading}
          data-testid="cleanup-loop-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-purple-700 hover:bg-purple-800 disabled:opacity-50 text-white text-sm font-medium">
          {loading && mode === 'cleanup-loop' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          🔁 Αυτόματο Loop (μέχρι 0 remaining)
        </button>
      </div>

      {/* v2.9.100 — Artist/Title Consolidation: auto-run στο nightly */}
      <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-4 mt-4">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-emerald-600 dark:text-emerald-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-emerald-900 dark:text-emerald-100">
              🎨 Συγχώνευση γραφών artist/title (Shazam canonical) — ΑΥΤΟΜΑΤΟ
            </h3>
            <p className="text-sm text-emerald-800 dark:text-emerald-200 mt-0.5">
              Τρέχει αυτόματα <strong>κάθε βράδυ στις 21:30</strong> ως μέρος του nightly pipeline.
              Βρίσκει records με ίδιο τραγούδι αλλά διαφορετική γραφή (π.χ. "Konstantinos Arguros"
              / "Argyros" / "Argiros") και τα μετονομάζει στη Shazam γραφή.
              <strong> Δεν χάνονται plays</strong>. Title-similarity guard ≥85% + audit log στο
              <code className="text-xs ml-1">consolidation_audit</code> collection.
            </p>
            {nightlyStatus?.last_summary?.steps && (
              <div className="mt-2 text-xs font-mono text-emerald-700 dark:text-emerald-300">
                {nightlyStatus.last_summary.steps
                  .filter(s => s.step === 'artist_title_consolidation')
                  .map((step, i) => (
                    <span key={i}>
                      🎨 Τελευταίο run: <strong>{(step.records_renamed || 0).toLocaleString('el-GR')}</strong> records renamed
                      · {step.groups_with_canonical} groups · skipped={step.title_mismatch_skipped} · {step.duration_s}s
                    </span>
                  ))
                }
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loop progress */}
      {loopProgress && (
        <div className="rounded-md bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-3 text-sm">
          <div className="flex items-center gap-2 font-semibold text-purple-700 dark:text-purple-300">
            <Sparkles className="w-4 h-4" />
            Loop: {loopProgress.status === 'done' ? 'Ολοκληρώθηκε ✓' : 'Σε εξέλιξη...'}
          </div>
          <div className="text-xs mt-1 text-purple-600 dark:text-purple-400">
            Rounds: {loopProgress.rounds} · Συγχωνεύτηκαν: {loopProgress.totalMerged.toLocaleString('el-GR')} · Plays recovered: {loopProgress.totalRecovered.toLocaleString('el-GR')}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 p-3 flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4" />
          Σφάλμα: {error}
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-4">
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 font-semibold mb-2">
            <CheckCircle2 className="w-5 h-5" />
            {mode === 'dry' && 'Αποτελέσματα Δοκιμής'}
            {mode === 'live' && 'Καθαρισμός Ολοκληρώθηκε'}
            {mode === 'cleanup-loop' && `Auto-Loop Ολοκληρώθηκε σε ${result._loop_rounds} γύρους`}
            {' · '}{result.duration_seconds}s
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Stat label="Σταθμοί που σαρώθηκαν" value={result.stations_scanned} />
            <Stat label="Plays που εξετάστηκαν" value={result.plays_examined} />
            <Stat label="Διπλά ζευγάρια" value={result.duplicate_pairs_found} highlight />
            <Stat label="Plays που συγχωνεύτηκαν" value={result.duplicates_merged} highlight />
          </div>
          {result.sample_merges?.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Δείγμα ({Math.min(result.sample_merges.length, 5)} από {result.sample_merges.length}):
              </div>
              <ul className="text-xs text-gray-700 dark:text-gray-300 space-y-1 font-mono">
                {result.sample_merges.slice(0, 5).map((s, i) => (
                  <li key={i}>
                    <span className="text-amber-700 dark:text-amber-300">{s.station}</span>
                    {' · '}{s.artist} — {s.title}
                    {' · '}<span className="text-gray-500">gap={s.gap_seconds}s</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, highlight }) => (
  <div className={`rounded p-2 ${highlight ? 'bg-emerald-100 dark:bg-emerald-900/40' : 'bg-white dark:bg-gray-700'}`}>
    <div className="text-[11px] text-gray-600 dark:text-gray-400">{label}</div>
    <div className={`text-lg font-semibold ${highlight ? 'text-emerald-800 dark:text-emerald-200' : 'text-gray-900 dark:text-gray-100'} tabular-nums`}>
      {(value ?? 0).toLocaleString('el-GR')}
    </div>
  </div>
);

export default DuplicateCleanupPanel;
